import { createSelector } from 'reselect';

import { RootState } from '../../reducers';
import { CodeElement, CodeList, CodeListNormalized, CodeTypeNames } from './code.types';

export const getCodeList = (state: RootState): { [key in CodeTypeNames]: CodeElement[] } =>
  (state.entities.code as any) || {};

export const getCodeListFilteredByType = createSelector(
  [getCodeList, (_state: RootState, props: { codeType: CodeTypeNames; includeInactive?: boolean }) => props],
  (codeList: CodeList, props): CodeElement[] =>
    props.includeInactive
      ? codeList[props.codeType] || []
      : (codeList[props.codeType] || []).filter((x) => !x.inActive),
);

export const getIngredientCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (groupList: CodeList, includeInactive = false) =>
    includeInactive
      ? groupList[CodeTypeNames.ingredientTypeCodes] || []
      : (groupList[CodeTypeNames.ingredientTypeCodes] || []).filter((x) => !x.inActive),
);

export const getCurrencyCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (groupList: CodeList, includeInactive: boolean) =>
    includeInactive
      ? groupList[CodeTypeNames.currencyCodes] || []
      : (groupList[CodeTypeNames.currencyCodes] || []).filter((x) => !x.inActive),
);

export const getCurrencyStrings = createSelector([getCurrencyCodeList], (codes: CodeElement[]) => {
  const symbols = codes.map((el) => el.description3);
  return symbols.filter((item, pos) => item && symbols.indexOf(item) === pos);
});

export const getCodeListFilteredByTypeNormalized = createSelector(
  [getCodeList],
  (codeList: CodeList) =>
    (props: { codeType: CodeTypeNames; includeInactive?: boolean }): CodeListNormalized => {
      const matchedCodeGroup: CodeElement[] = props.includeInactive
        ? codeList[props.codeType] || []
        : (codeList[props.codeType] || []).filter((x) => !x.inActive);

      return matchedCodeGroup.reduce((acc, curr) => ({ ...acc, [curr.code]: curr }), {}) || {};
    },
);

export const getPriceUnitByCode = createSelector(
  [getCodeList],
  (codeList: CodeList) =>
    (code: number): CodeElement | undefined => {
      const priceUnitCodes = codeList[CodeTypeNames.priceUnitCodes] || [];
      return priceUnitCodes.find((codeEl) => codeEl.code === code);
    },
);

export const getLotStatusCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (codeList: CodeList, includeInactive: boolean) =>
    includeInactive
      ? codeList[CodeTypeNames.lotStatusCodes] || []
      : (codeList[CodeTypeNames.lotStatusCodes] || []).filter((x) => !x.inActive),
);

export const getLotClassCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (codeList: CodeList, includeInactive: boolean) =>
    includeInactive
      ? codeList[CodeTypeNames.lotClassCodes] || []
      : (codeList[CodeTypeNames.lotClassCodes] || []).filter((x) => !x.inActive),
);

export const getLotMarketClassCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (codeList: CodeList, includeInactive: boolean) =>
    includeInactive
      ? codeList[CodeTypeNames.lotMarketClassCodes] || []
      : (codeList[CodeTypeNames.lotMarketClassCodes] || []).filter((x) => !x.inActive),
);

export const getLotSexCodeList = createSelector(
  [getCodeList, (_state: RootState, includeInactive: boolean) => includeInactive],
  (codeList: CodeList, includeInactive: boolean) =>
    includeInactive
      ? codeList[CodeTypeNames.lotSexCodes] || []
      : (codeList[CodeTypeNames.lotSexCodes] || []).filter((x) => !x.inActive),
);
